// Mock data for Azure Entra ID and Microsoft 365 integration
import { 
  AzureADUser, 
  M365License, 
  AzureADGroup,
  ConditionalAccessPolicy,
  PrivilegedRole,
  AccessReview,
  AzureADSyncStatus,
  AuthenticationMethod,
  PIMAlert,
  PIMAuditEvent,
  AccessPackageCatalog,
  AccessPackage,
  TermsOfUse
} from '../types/azureEntraId';
import { COMPANY_INFO, GLOBAL_LOCATIONS, DEPARTMENTS, JOB_LEVELS } from '../constants/enterpriseData';

// Mock Azure AD Users with M365 licenses - Global Healthcare Enterprise
export const mockAzureADUsers: AzureADUser[] = [
  {
    id: 'aad_user_1',
    objectId: '9f8b7c6d-5e4f-3a2b-1c9d-8e7f6a5b4c3d',
    userPrincipalName: 'sarah.müller@cloudonmars.com',
    displayName: 'Dr. Sarah Müller',
    givenName: 'Sarah',
    surname: 'Müller',
    mail: 'sarah.müller@cloudonmars.com',
    mobilePhone: '+49-6172-609-2500',
    jobTitle: 'Chief Executive Officer',
    department: 'Corporate Functions',
    officeLocation: 'Bad Homburg, Germany',
    companyName: COMPANY_INFO.name,
    accountEnabled: true,
    usageLocation: 'DE',
    preferredLanguage: 'de-DE',
    createdDateTime: new Date('2020-01-15'),
    lastSignInDateTime: new Date('2024-03-20T09:15:00Z'),
    onPremisesSyncEnabled: false,
    proxyAddresses: ['SMTP:sarah.müller@cloudonmars.com', 'smtp:ceo@cloudonmars.com'],
    assignedLicenses: [
      {
        id: 'license_1',
        skuId: '6fd2c87f-b296-42f0-b197-1e91e994b900',
        skuPartNumber: 'ENTERPRISEPREMIUM',
        name: 'Microsoft 365 E5',
        servicePlans: [
          {
            id: 'sp_1',
            servicePlanId: 'efb87545-963c-4e0d-99df-69c6916d9eb0',
            servicePlanName: 'EXCHANGE_S_ENTERPRISE',
            provisioningStatus: 'Success'
          },
          {
            id: 'sp_2',
            servicePlanId: '5dbe027f-2339-4123-9542-606e4d348a72',
            servicePlanName: 'SHAREPOINTENTERPRISE',
            provisioningStatus: 'Success'
          },
          {
            id: 'sp_3',
            servicePlanId: '882e1d05-acd1-4ccb-8708-6ee03664b117',
            servicePlanName: 'TEAMS1',
            provisioningStatus: 'Success'
          }
        ],
        assignedDateTime: new Date('2020-01-15'),
        state: 'Active',
      }
    ],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_1',
        type: 'authenticator',
        displayName: 'Microsoft Authenticator',
        createdDateTime: new Date('2020-01-15'),
        isUsable: true,
        isPrimary: true,
        lastUsedDateTime: new Date('2024-03-20T09:15:00Z')
      },
      {
        id: 'auth_2',
        type: 'fido2',
        displayName: 'YubiKey 5C',
        createdDateTime: new Date('2022-06-10'),
        detail: 'YubiKey 5C NFC',
        isUsable: true,
        isPrimary: false,
        lastUsedDateTime: new Date('2024-03-19T14:30:00Z')
      }
    ],
    riskLevel: 'none',
    complianceStatus: {
      isCompliant: true,
      complianceState: 'compliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_2',
    objectId: '8e7a6b5c-4d3f-2e1a-9b8c-7d6e5f4a3b2c',
    userPrincipalName: 'james.washington@cloudonmars.com',
    displayName: 'James Washington',
    givenName: 'James',
    surname: 'Washington',
    mail: 'james.washington@cloudonmars.com',
    mobilePhone: '+1-781-555-0102',
    jobTitle: 'Chief Technology Officer',
    department: 'Information Technology',
    officeLocation: 'Waltham, MA',
    companyName: COMPANY_INFO.name,
    accountEnabled: true,
    usageLocation: 'US',
    preferredLanguage: 'en-US',
    createdDateTime: new Date('2020-03-20'),
    lastSignInDateTime: new Date('2024-03-20T08:45:00Z'),
    onPremisesSyncEnabled: true,
    onPremisesImmutableId: 'AAD_8e7a6b5c',
    proxyAddresses: ['SMTP:james.washington@cloudonmars.com'],
    assignedLicenses: [
      {
        id: 'license_2',
        skuId: '06ebc4ee-1bb5-47dd-8120-11324bc54e06',
        skuPartNumber: 'SPE_E5',
        name: 'Microsoft 365 E5',
        servicePlans: [
          {
            id: 'sp_4',
            servicePlanId: 'efb87545-963c-4e0d-99df-69c6916d9eb0',
            servicePlanName: 'EXCHANGE_S_ENTERPRISE',
            provisioningStatus: 'Success'
          }
        ],
        assignedDateTime: new Date('2020-03-20'),
        state: 'Active',
      }
    ],
    memberOf: [],
    manager: undefined,
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_3',
        type: 'phone',
        displayName: 'SMS to +1...0102',
        createdDateTime: new Date('2020-03-20'),
        detail: '+1-781-555-0102',
        isUsable: true,
        isPrimary: true,
        lastUsedDateTime: new Date('2024-03-15T10:20:00Z')
      }
    ],
    riskLevel: 'low',
    complianceStatus: {
      isCompliant: true,
      complianceState: 'compliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_3',
    objectId: '7d6e5f4a-3b2c-1d9e-8a7b-6c5d4e3f2a1b',
    userPrincipalName: 'priya.sharma@cloudonmars.com',
    displayName: 'Dr. Priya Sharma',
    givenName: 'Priya',
    surname: 'Sharma',
    mail: 'priya.sharma@cloudonmars.com',
    mobilePhone: '+91-80-2555-0103',
    jobTitle: 'VP Clinical Affairs',
    department: 'Clinical Affairs & Research',
    officeLocation: 'Bangalore, India',
    companyName: COMPANY_INFO.name,
    accountEnabled: true,
    usageLocation: 'IN',
    preferredLanguage: 'en-IN',
    createdDateTime: new Date('2021-02-10'),
    lastSignInDateTime: new Date('2024-03-20T07:30:00Z'),
    onPremisesSyncEnabled: false,
    proxyAddresses: ['SMTP:priya.sharma@cloudonmars.com'],
    assignedLicenses: [
      {
        id: 'license_3',
        skuId: '05e9a617-0261-4cee-bb44-138d3ef5d965',
        skuPartNumber: 'SPE_E3',
        name: 'Microsoft 365 E3',
        servicePlans: [],
        assignedDateTime: new Date('2021-02-10'),
        state: 'Active',
      }
    ],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_4',
        type: 'authenticator',
        displayName: 'Microsoft Authenticator',
        createdDateTime: new Date('2021-02-10'),
        isUsable: true,
        isPrimary: true,
        lastUsedDateTime: new Date('2024-03-20T07:30:00Z')
      }
    ],
    riskLevel: 'none',
    complianceStatus: {
      isCompliant: true,
      complianceState: 'compliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_4',
    objectId: '6c5d4e3f-2a1b-9d8e-7a6b-5c4d3e2f1a0b',
    userPrincipalName: 'maria.gonzalez@partner-clinics.com',
    displayName: 'Dr. Maria Gonzalez (External)',
    givenName: 'Maria',
    surname: 'Gonzalez',
    mail: 'maria.gonzalez@partner-clinics.com',
    jobTitle: 'External Clinical Consultant',
    department: 'External Partners',
    companyName: 'Partner Medical Clinics',
    accountEnabled: true,
    usageLocation: 'ES',
    preferredLanguage: 'es-ES',
    createdDateTime: new Date('2024-01-15'),
    lastSignInDateTime: new Date('2024-03-18T14:20:00Z'),
    onPremisesSyncEnabled: false,
    proxyAddresses: [],
    assignedLicenses: [],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_5',
        type: 'email',
        displayName: 'Email OTP',
        createdDateTime: new Date('2024-01-15'),
        detail: 'maria.gonzalez@partner-clinics.com',
        isUsable: true,
        isPrimary: true
      }
    ],
    riskLevel: 'medium',
    complianceStatus: {
      isCompliant: false,
      complianceState: 'nonCompliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_5',
    objectId: '5b4c3d2e-1f0a-8c7d-6a5b-4c3d2e1f0a9b',
    userPrincipalName: 'service.datapipeline@cloudonmars.com',
    displayName: 'Data Pipeline Service Account',
    givenName: 'Data Pipeline',
    surname: 'Service',
    mail: 'datapipeline-admin@cloudonmars.com',
    department: 'Information Technology',
    companyName: COMPANY_INFO.name,
    accountEnabled: true,
    createdDateTime: new Date('2020-01-01'),
    onPremisesSyncEnabled: false,
    proxyAddresses: [],
    assignedLicenses: [],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_6',
        type: 'password',
        displayName: 'Password',
        createdDateTime: new Date('2020-01-01'),
        isUsable: true,
        isPrimary: true
      }
    ],
    riskLevel: 'high',
    complianceStatus: {
      isCompliant: false,
      complianceState: 'nonCompliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_6',
    objectId: '4a3b2c1d-0e9f-7c6d-5a4b-3c2d1e0f9a8b',
    userPrincipalName: 'liu.wei@cloudonmars.com',
    displayName: 'Liu Wei',
    givenName: 'Wei',
    surname: 'Liu',
    mail: 'liu.wei@cloudonmars.com',
    mobilePhone: '+86-10-5555-0104',
    jobTitle: 'Senior Director Manufacturing',
    department: 'Production',
    officeLocation: 'Beijing, China',
    companyName: COMPANY_INFO.name,
    accountEnabled: true,
    usageLocation: 'CN',
    preferredLanguage: 'zh-CN',
    createdDateTime: new Date('2019-06-15'),
    lastSignInDateTime: new Date('2024-03-20T02:15:00Z'),
    onPremisesSyncEnabled: true,
    onPremisesImmutableId: 'AAD_4a3b2c1d',
    proxyAddresses: ['SMTP:liu.wei@cloudonmars.com'],
    assignedLicenses: [
      {
        id: 'license_6',
        skuId: '05e9a617-0261-4cee-bb44-138d3ef5d965',
        skuPartNumber: 'SPE_E3',
        name: 'Microsoft 365 E3',
        servicePlans: [],
        assignedDateTime: new Date('2019-06-15'),
        state: 'Active',
      }
    ],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_7',
        type: 'authenticator',
        displayName: 'Microsoft Authenticator',
        createdDateTime: new Date('2019-06-15'),
        isUsable: true,
        isPrimary: true,
        lastUsedDateTime: new Date('2024-03-20T02:15:00Z')
      }
    ],
    riskLevel: 'none',
    complianceStatus: {
      isCompliant: true,
      complianceState: 'compliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_7',
    objectId: '3a2b1c0d-9e8f-6c5d-4a3b-2c1d0e9f8a7b',
    userPrincipalName: 'ana.silva@cloudonmars.com',
    displayName: 'Ana Silva',
    givenName: 'Ana',
    surname: 'Silva',
    mail: 'ana.silva@cloudonmars.com',
    mobilePhone: '+55-11-5555-0105',
    jobTitle: 'Regional VP Latin America',
    department: 'Clinical Operations',
    officeLocation: 'São Paulo, Brazil',
    companyName: COMPANY_INFO.name,
    accountEnabled: true,
    usageLocation: 'BR',
    preferredLanguage: 'pt-BR',
    createdDateTime: new Date('2021-08-20'),
    lastSignInDateTime: new Date('2024-03-19T18:45:00Z'),
    onPremisesSyncEnabled: false,
    proxyAddresses: ['SMTP:ana.silva@cloudonmars.com'],
    assignedLicenses: [
      {
        id: 'license_7',
        skuId: '6fd2c87f-b296-42f0-b197-1e91e994b900',
        skuPartNumber: 'ENTERPRISEPREMIUM',
        name: 'Microsoft 365 E5',
        servicePlans: [],
        assignedDateTime: new Date('2021-08-20'),
        state: 'Active',
      }
    ],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_8',
        type: 'fido2',
        displayName: 'YubiKey Security Key',
        createdDateTime: new Date('2022-03-10'),
        detail: 'YubiKey 5 NFC',
        isUsable: true,
        isPrimary: false,
        lastUsedDateTime: new Date('2024-03-19T18:45:00Z')
      }
    ],
    riskLevel: 'none',
    complianceStatus: {
      isCompliant: true,
      complianceState: 'compliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_3',
    objectId: '7d6e5f4a-3b2c-1d9e-8a7b-6c5d4e3f2a1b',
    userPrincipalName: 'emily.watson@cloudonmars.com',
    displayName: 'Emily Watson',
    givenName: 'Emily',
    surname: 'Watson',
    mail: 'emily.watson@cloudonmars.com',
    jobTitle: 'HR Director',
    department: 'Human Resources',
    officeLocation: 'London Office',
    companyName: 'Clouds on Mars',
    accountEnabled: true,
    usageLocation: 'GB',
    preferredLanguage: 'en-GB',
    createdDateTime: new Date('2021-02-10'),
    lastSignInDateTime: new Date('2024-03-20T07:30:00Z'),
    onPremisesSyncEnabled: false,
    proxyAddresses: ['SMTP:emily.watson@cloudonmars.com'],
    assignedLicenses: [
      {
        id: 'license_3',
        skuId: '05e9a617-0261-4cee-bb44-138d3ef5d965',
        skuPartNumber: 'SPE_E3',
        name: 'Microsoft 365 E3',
        servicePlans: [],
        assignedDateTime: new Date('2021-02-10'),
        state: 'Active',
      }
    ],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_4',
        type: 'authenticator',
        displayName: 'Microsoft Authenticator',
        createdDateTime: new Date('2021-02-10'),
        isUsable: true,
        isPrimary: true,
        lastUsedDateTime: new Date('2024-03-20T07:30:00Z')
      }
    ],
    riskLevel: 'none',
    complianceStatus: {
      isCompliant: true,
      complianceState: 'compliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_4',
    objectId: '6c5d4e3f-2a1b-9d8e-7a6b-5c4d3e2f1a0b',
    userPrincipalName: 'guest.user@external.com',
    displayName: 'Guest User (External)',
    givenName: 'Guest',
    surname: 'User',
    mail: 'guest.user@external.com',
    jobTitle: 'Consultant',
    department: 'External',
    companyName: 'External Partner Inc.',
    accountEnabled: true,
    createdDateTime: new Date('2024-01-15'),
    lastSignInDateTime: new Date('2024-03-18T14:20:00Z'),
    onPremisesSyncEnabled: false,
    proxyAddresses: [],
    assignedLicenses: [],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_5',
        type: 'email',
        displayName: 'Email OTP',
        createdDateTime: new Date('2024-01-15'),
        detail: 'guest.user@external.com',
        isUsable: true,
        isPrimary: true
      }
    ],
    riskLevel: 'medium',
    complianceStatus: {
      isCompliant: false,
      complianceState: 'nonCompliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  },
  {
    id: 'aad_user_5',
    objectId: '5b4c3d2e-1f0a-8c7d-6a5b-4c3d2e1f0a9b',
    userPrincipalName: 'service.account@cloudonmars.com',
    displayName: 'HR Service Account',
    givenName: 'HR',
    surname: 'Service',
    mail: 'hr-service@cloudonmars.com',
    department: 'IT Services',
    companyName: 'Clouds on Mars',
    accountEnabled: true,
    createdDateTime: new Date('2020-01-01'),
    onPremisesSyncEnabled: false,
    proxyAddresses: [],
    assignedLicenses: [],
    memberOf: [],
    directReports: [],
    authenticationMethods: [
      {
        id: 'auth_6',
        type: 'password',
        displayName: 'Password',
        createdDateTime: new Date('2020-01-01'),
        isUsable: true,
        isPrimary: true
      }
    ],
    riskLevel: 'high',
    complianceStatus: {
      isCompliant: false,
      complianceState: 'nonCompliant',
      lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
      policies: []
    }
  }
];

// Mock Azure AD Groups - Global Healthcare Enterprise
export const mockAzureADGroups: AzureADGroup[] = [
  {
    id: 'group_1',
    objectId: 'g_9f8b7c6d-5e4f-3a2b-1c9d-8e7f6a5b4c3d',
    displayName: 'All Employees Global',
    description: 'All global employees across all locations',
    mail: 'allemployees@cloudonmars.com',
    mailEnabled: true,
    securityEnabled: true,
    groupTypes: ['Unified'],
    createdDateTime: new Date('2020-01-01'),
    memberCount: COMPANY_INFO.employees,
    owners: ['sarah.müller@cloudonmars.com'],
    classification: 'Public',
    dynamicMembership: true,
    membershipRule: "(user.department -ne null) and (user.accountEnabled -eq true) and (user.companyName -eq 'Clouds on Mars')",
    membershipRuleProcessingState: 'On',
    onPremisesSyncEnabled: false,
    teamEnabled: true,
    visibility: 'Public'
  },
  {
    id: 'group_2',
    objectId: 'g_8e7a6b5c-4d3f-2e1a-9b8c-7d6e5f4a3b2c',
    displayName: 'IT Administrators',
    description: 'Global and Azure AD administrators',
    securityEnabled: true,
    mailEnabled: false,
    groupTypes: [],
    createdDateTime: new Date('2020-01-01'),
    memberCount: 12,
    owners: ['michael.rodriguez@cloudonmars.com'],
    classification: 'Restricted',
    dynamicMembership: false,
    onPremisesSyncEnabled: true,
    visibility: 'Private'
  },
  {
    id: 'group_3',
    objectId: 'g_7d6e5f4a-3b2c-1d9e-8a7b-6c5d4e3f2a1b',
    displayName: 'HR Team',
    description: 'Human Resources department',
    mail: 'hr@cloudonmars.com',
    mailEnabled: true,
    securityEnabled: true,
    groupTypes: ['Unified', 'DynamicMembership'],
    membershipRule: "(user.department -eq \"Human Resources\")",
    membershipRuleProcessingState: 'On',
    createdDateTime: new Date('2020-02-15'),
    memberCount: 24,
    owners: ['emily.watson@cloudonmars.com'],
    classification: 'Confidential',
    dynamicMembership: true,
    onPremisesSyncEnabled: false,
    teamEnabled: true,
    visibility: 'Private'
  },
  {
    id: 'group_4',
    objectId: 'g_6c5d4e3f-2a1b-9d8e-7a6b-5c4d3e2f1a0b',
    displayName: 'Executive Leadership',
    description: 'C-Suite and VP level executives',
    securityEnabled: true,
    mailEnabled: true,
    mail: 'executives@cloudonmars.com',
    groupTypes: [],
    createdDateTime: new Date('2020-01-01'),
    memberCount: 8,
    owners: ['sarah.chen@cloudonmars.com'],
    classification: 'Restricted',
    dynamicMembership: false,
    onPremisesSyncEnabled: false,
    visibility: 'HiddenMembership'
  },
  {
    id: 'group_5',
    objectId: 'g_5b4c3d2e-1f0a-8c7d-6a5b-4c3d2e1f0a9b',
    displayName: 'External Guests',
    description: 'External guest users and partners',
    securityEnabled: true,
    mailEnabled: false,
    groupTypes: ['DynamicMembership'],
    membershipRule: "(user.userType -eq \"Guest\")",
    membershipRuleProcessingState: 'On',
    createdDateTime: new Date('2021-05-20'),
    memberCount: 47,
    owners: ['michael.rodriguez@cloudonmars.com'],
    classification: 'Public',
    dynamicMembership: true,
    onPremisesSyncEnabled: false,
    visibility: 'Public'
  }
];

// Mock Conditional Access Policies
export const mockConditionalAccessPolicies: ConditionalAccessPolicy[] = [
  {
    id: 'cap_1',
    displayName: 'Require MFA for all users',
    state: 'enabled',
    createdDateTime: new Date('2020-01-15'),
    modifiedDateTime: new Date('2024-01-10'),
    conditions: {
      users: {
        includeUsers: ['All'],
        excludeUsers: [],
        includeGroups: [],
        excludeGroups: ['g_5b4c3d2e-1f0a-8c7d-6a5b-4c3d2e1f0a9b'], // Exclude service accounts
        includeRoles: [],
        excludeRoles: []
      },
      applications: {
        includeApplications: ['All'],
        excludeApplications: []
      },
      platforms: {
        includePlatforms: ['all'],
        excludePlatforms: []
      },
      clientAppTypes: ['browser', 'mobileAppsAndDesktopClients'],
      signInRiskLevels: ['medium', 'high']
    },
    grantControls: {
      operator: 'OR',
      builtInControls: ['mfa'],
      customAuthenticationFactors: [],
      termsOfUse: []
    }
  },
  {
    id: 'cap_2',
    displayName: 'Block legacy authentication',
    state: 'enabled',
    createdDateTime: new Date('2020-03-01'),
    modifiedDateTime: new Date('2023-11-15'),
    conditions: {
      users: {
        includeUsers: ['All'],
        excludeUsers: [],
        includeGroups: [],
        excludeGroups: [],
        includeRoles: [],
        excludeRoles: []
      },
      applications: {
        includeApplications: ['All'],
        excludeApplications: []
      },
      clientAppTypes: ['exchangeActiveSync', 'other']
    },
    grantControls: {
      operator: 'OR',
      builtInControls: ['block'],
      customAuthenticationFactors: [],
      termsOfUse: []
    }
  },
  {
    id: 'cap_3',
    displayName: 'Require compliant devices for Office 365',
    state: 'enabled',
    createdDateTime: new Date('2021-06-15'),
    modifiedDateTime: new Date('2024-02-20'),
    conditions: {
      users: {
        includeUsers: [],
        excludeUsers: [],
        includeGroups: ['g_9f8b7c6d-5e4f-3a2b-1c9d-8e7f6a5b4c3d'], // All Employees
        excludeGroups: ['g_5b4c3d2e-1f0a-8c7d-6a5b-4c3d2e1f0a9b'], // External Guests
        includeRoles: [],
        excludeRoles: []
      },
      applications: {
        includeApplications: ['Office365'],
        excludeApplications: []
      },
      platforms: {
        includePlatforms: ['iOS', 'android', 'windows', 'macOS'],
        excludePlatforms: []
      },
      clientAppTypes: ['mobileAppsAndDesktopClients']
    },
    grantControls: {
      operator: 'AND',
      builtInControls: ['compliantDevice', 'mfa'],
      customAuthenticationFactors: [],
      termsOfUse: []
    },
    sessionControls: {
      cloudAppSecurity: {
        cloudAppSecurityType: 'monitorOnly',
        isEnabled: true
      },
      signInFrequency: {
        value: 7,
        type: 'days',
        isEnabled: true
      }
    }
  },
  {
    id: 'cap_4',
    displayName: 'Privileged Admin Access Control',
    state: 'enabled',
    createdDateTime: new Date('2020-01-20'),
    modifiedDateTime: new Date('2024-03-01'),
    conditions: {
      users: {
        includeUsers: [],
        excludeUsers: [],
        includeGroups: [],
        excludeGroups: [],
        includeRoles: ['62e90394-69f5-4237-9190-012177145e10', 'f28a1f50-f6e7-4571-818b-6a12f2af6b6c'], // Global Admin, SharePoint Admin
        excludeRoles: []
      },
      applications: {
        includeApplications: ['All'],
        excludeApplications: []
      },
      locations: {
        includeLocations: ['All'],
        excludeLocations: ['AllTrusted']
      },
      clientAppTypes: ['browser', 'mobileAppsAndDesktopClients']
    },
    grantControls: {
      operator: 'AND',
      builtInControls: ['mfa', 'compliantDevice'],
      customAuthenticationFactors: [],
      termsOfUse: []
    },
    sessionControls: {
      persistentBrowser: {
        mode: 'never',
        isEnabled: true
      },
      signInFrequency: {
        value: 4,
        type: 'hours',
        isEnabled: true
      }
    }
  }
];

// Mock Privileged Roles (PIM)
export const mockPrivilegedRoles: PrivilegedRole[] = [
  {
    id: 'role_1',
    displayName: 'Global Administrator',
    description: 'Can manage all aspects of Azure AD and Microsoft services that use Azure AD identities.',
    templateId: '62e90394-69f5-4237-9190-012177145e10',
    isBuiltIn: true,
    isEnabled: true,
    activeAssignments: [
      {
        id: 'assign_1',
        principalId: '9f8b7c6d-5e4f-3a2b-1c9d-8e7f6a5b4c3d',
        principalName: 'Sarah Chen',
        principalType: 'User',
        roleDefinitionId: 'role_1',
        directoryScopeId: '/',
        status: 'Active',
        startDateTime: new Date('2020-01-15'),
        memberType: 'Direct'
      }
    ],
    eligibleAssignments: [
      {
        id: 'assign_2',
        principalId: '8e7a6b5c-4d3f-2e1a-9b8c-7d6e5f4a3b2c',
        principalName: 'Michael Rodriguez',
        principalType: 'User',
        roleDefinitionId: 'role_1',
        directoryScopeId: '/',
        status: 'Provisioned',
        memberType: 'Eligible',
        justification: 'Break glass account for emergencies'
      }
    ],
    settings: {
      maximumDuration: 'PT8H',
      requireMfa: true,
      requireJustification: true,
      requireTicketInfo: true,
      requireApproval: true,
      approvers: ['sarah.chen@cloudonmars.com'],
      notificationSettings: {
        notifyOnActivation: true,
        notifyOnAssignment: true,
        notifyOnEligibleActivation: true,
        escalationLevel: 2
      },
      activationRequirement: {
        maximumActivationDuration: 'PT4H',
        requireMfaOnActivation: true,
        requireJustificationOnActivation: true,
        requireTicketInfoOnActivation: true,
        requireApprovalToActivate: true
      }
    }
  },
  {
    id: 'role_2',
    displayName: 'User Administrator',
    description: 'Can manage all aspects of users and groups, including resetting passwords for limited admins.',
    templateId: 'fe930be7-5e62-47db-91af-98c3a49a38b1',
    isBuiltIn: true,
    isEnabled: true,
    activeAssignments: [
      {
        id: 'assign_3',
        principalId: '7d6e5f4a-3b2c-1d9e-8a7b-6c5d4e3f2a1b',
        principalName: 'Emily Watson',
        principalType: 'User',
        roleDefinitionId: 'role_2',
        directoryScopeId: '/',
        status: 'Active',
        startDateTime: new Date('2021-02-10'),
        memberType: 'Direct'
      }
    ],
    eligibleAssignments: [],
    settings: {
      maximumDuration: 'PT12H',
      requireMfa: true,
      requireJustification: true,
      requireTicketInfo: false,
      requireApproval: false,
      approvers: [],
      notificationSettings: {
        notifyOnActivation: true,
        notifyOnAssignment: true,
        notifyOnEligibleActivation: false,
        escalationLevel: 1
      },
      activationRequirement: {
        maximumActivationDuration: 'PT8H',
        requireMfaOnActivation: true,
        requireJustificationOnActivation: true,
        requireTicketInfoOnActivation: false,
        requireApprovalToActivate: false
      }
    }
  }
];

// Mock Access Reviews
export const mockAccessReviews: AccessReview[] = [
  {
    id: 'review_1',
    displayName: 'Quarterly Guest User Access Review',
    description: 'Review external guest user access to company resources',
    scope: {
      query: "/groups/g_5b4c3d2e-1f0a-8c7d-6a5b-4c3d2e1f0a9b/members",
      queryType: 'MicrosoftGraph'
    },
    reviewers: [
      {
        id: 'reviewer_1',
        displayName: 'Michael Rodriguez',
        userPrincipalName: 'michael.rodriguez@cloudonmars.com',
        type: 'user'
      }
    ],
    settings: {
      recurrence: {
        pattern: 'quarterly',
        range: {
          type: 'noEnd'
        }
      },
      reminderSettings: {
        reminderEnabled: true,
        reminderPercentage: 75
      },
      mailNotificationsEnabled: true,
      autoApplyDecisionsEnabled: true,
      justificationRequired: true,
      defaultDecision: 'Deny'
    },
    status: 'InProgress',
    startDateTime: new Date('2024-03-01'),
    endDateTime: new Date('2024-03-31'),
    decisions: [
      {
        id: 'decision_1',
        accessReviewId: 'review_1',
        reviewedBy: 'michael.rodriguez@cloudonmars.com',
        reviewedDateTime: new Date('2024-03-15'),
        decision: 'Approve',
        justification: 'Still working on active project',
        target: {
          id: '6c5d4e3f-2a1b-9d8e-7a6b-5c4d3e2f1a0b',
          displayName: 'Guest User',
          type: 'user'
        }
      }
    ]
  },
  {
    id: 'review_2',
    displayName: 'Admin Role Assignment Review',
    description: 'Monthly review of privileged admin role assignments',
    scope: {
      query: "/roleManagement/directory/roleAssignments?$filter=roleDefinitionId eq 'role_1'",
      queryType: 'MicrosoftGraph'
    },
    reviewers: [
      {
        id: 'reviewer_2',
        displayName: 'Security Team',
        type: 'group'
      }
    ],
    settings: {
      recurrence: {
        pattern: 'monthly',
        range: {
          type: 'noEnd'
        }
      },
      reminderSettings: {
        reminderEnabled: true,
        reminderPercentage: 50
      },
      mailNotificationsEnabled: true,
      autoApplyDecisionsEnabled: false,
      justificationRequired: true,
      defaultDecision: 'None'
    },
    status: 'Scheduled',
    startDateTime: new Date('2024-04-01'),
    endDateTime: new Date('2024-04-15'),
    decisions: []
  }
];

// Mock Azure AD Sync Status
export const mockAzureADSyncStatus: AzureADSyncStatus = {
  isDirSyncEnabled: true,
  lastSyncDateTime: new Date('2024-03-20T06:00:00Z'),
  lastPasswordSyncDateTime: new Date('2024-03-20T06:00:00Z'),
  syncInterval: 30, // minutes
  syncErrors: [
    {
      id: 'error_1',
      timestamp: new Date('2024-03-19T14:30:00Z'),
      errorCode: 'AttributeValueMustBeUnique',
      errorMessage: 'Unable to update user: Duplicate UserPrincipalName',
      affectedObject: 'john.doe@cloudonmars.com',
      resolution: 'Resolve duplicate UPN in on-premises Active Directory',
      severity: 'Warning'
    },
    {
      id: 'error_2',
      timestamp: new Date('2024-03-18T10:15:00Z'),
      errorCode: 'FederatedDomainChangeError',
      errorMessage: 'Cannot change federated domain for user',
      affectedObject: 'jane.smith@cloudonmars.com',
      resolution: 'Update domain in source directory',
      severity: 'Error'
    }
  ],
  pendingChanges: 12,
  syncType: 'Delta',
  status: 'Idle'
};

// Mock PIM Alerts
export const mockPIMAlerts: PIMAlert[] = [
  {
    id: 'pim_alert_1',
    alertType: 'TooManyGlobalAdminsAssignedToTenantAlert',
    alertName: 'Too many Global Administrators',
    severity: 'Medium',
    isActive: true,
    lastScannedDateTime: new Date('2024-03-20T00:00:00Z'),
    incidentCount: 1,
    threshold: 3,
    alertConfiguration: {
      minimumActivations: 3
    }
  },
  {
    id: 'pim_alert_2',
    alertType: 'RolesAssignedOutsidePimAlert',
    alertName: 'Roles assigned outside of PIM',
    severity: 'High',
    isActive: true,
    lastScannedDateTime: new Date('2024-03-20T00:00:00Z'),
    incidentCount: 2,
    alertConfiguration: {}
  },
  {
    id: 'pim_alert_3',
    alertType: 'SequentialActivationRenewalsAlert',
    alertName: 'Sequential role activations',
    severity: 'Low',
    isActive: false,
    lastScannedDateTime: new Date('2024-03-19T00:00:00Z'),
    incidentCount: 0,
    threshold: 5,
    alertConfiguration: {
      duration: 'PT1H',
      minimumActivations: 5
    }
  }
];

// Mock PIM Audit Events
export const mockPIMAuditEvents: PIMAuditEvent[] = [
  {
    id: 'audit_1',
    correlationId: 'corr_123456',
    eventName: 'Activate role',
    initiatedBy: {
      user: {
        id: '8e7a6b5c-4d3f-2e1a-9b8c-7d6e5f4a3b2c',
        displayName: 'Michael Rodriguez',
        userPrincipalName: 'michael.rodriguez@cloudonmars.com'
      }
    },
    targetResources: [
      {
        id: 'role_1',
        displayName: 'Global Administrator',
        type: 'Role',
        modifiedProperties: [
          {
            displayName: 'Status',
            oldValue: 'Eligible',
            newValue: 'Active'
          }
        ]
      }
    ],
    additionalDetails: [
      { key: 'Justification', value: 'Emergency access for production issue' },
      { key: 'TicketNumber', value: 'INC-2024-0320' },
      { key: 'Duration', value: '4 hours' }
    ],
    result: 'success',
    activityDateTime: new Date('2024-03-20T02:15:00Z'),
    activityDisplayName: 'Activate eligible role assignment',
    category: 'RoleManagement'
  },
  {
    id: 'audit_2',
    correlationId: 'corr_123457',
    eventName: 'Add eligible member to role',
    initiatedBy: {
      user: {
        id: '9f8b7c6d-5e4f-3a2b-1c9d-8e7f6a5b4c3d',
        displayName: 'Sarah Chen',
        userPrincipalName: 'sarah.chen@cloudonmars.com'
      }
    },
    targetResources: [
      {
        id: '8e7a6b5c-4d3f-2e1a-9b8c-7d6e5f4a3b2c',
        displayName: 'Michael Rodriguez',
        type: 'User'
      }
    ],
    additionalDetails: [
      { key: 'Role', value: 'Global Administrator' },
      { key: 'Assignment Type', value: 'Eligible' }
    ],
    result: 'success',
    activityDateTime: new Date('2024-03-15T10:30:00Z'),
    activityDisplayName: 'Add eligible member to role',
    category: 'RoleManagement'
  },
  {
    id: 'audit_3',
    correlationId: 'corr_123458',
    eventName: 'Deactivate role',
    initiatedBy: {
      app: {
        appId: 'pim-service',
        displayName: 'Azure AD PIM Service'
      }
    },
    targetResources: [
      {
        id: 'role_1',
        displayName: 'Global Administrator',
        type: 'Role'
      }
    ],
    additionalDetails: [
      { key: 'Reason', value: 'Activation expired' },
      { key: 'User', value: 'michael.rodriguez@cloudonmars.com' }
    ],
    result: 'success',
    resultReason: 'Automatic deactivation after 4 hours',
    activityDateTime: new Date('2024-03-20T06:15:00Z'),
    activityDisplayName: 'Deactivate role assignment',
    category: 'RoleManagement'
  }
];

// Mock Access Package Catalogs
export const mockAccessPackageCatalogs: AccessPackageCatalog[] = [
  {
    id: 'catalog_1',
    displayName: 'General Access Catalog',
    description: 'Default catalog for standard access packages',
    catalogType: 'ServiceDefault',
    state: 'published',
    isExternallyVisible: false,
    createdDateTime: new Date('2020-01-01'),
    modifiedDateTime: new Date('2024-01-15'),
    accessPackageCount: 5
  },
  {
    id: 'catalog_2',
    displayName: 'Guest Access Catalog',
    description: 'Access packages for external users and partners',
    catalogType: 'UserManaged',
    state: 'published',
    isExternallyVisible: true,
    createdDateTime: new Date('2021-06-01'),
    modifiedDateTime: new Date('2024-03-01'),
    accessPackageCount: 3
  }
];

// Mock Access Packages
export const mockAccessPackages: AccessPackage[] = [
  {
    id: 'package_1',
    catalogId: 'catalog_1',
    displayName: 'Marketing Team Access',
    description: 'Access to marketing resources and applications',
    isHidden: false,
    createdDateTime: new Date('2021-01-15'),
    modifiedDateTime: new Date('2024-02-20'),
    resourceScopes: [
      {
        id: 'resource_1',
        displayName: 'Marketing SharePoint Site',
        description: 'Team collaboration site',
        resourceType: 'sharePointOnline',
        originId: 'https://cloudonmars.sharepoint.com/sites/marketing',
        originSystem: 'SharePointOnline'
      },
      {
        id: 'resource_2',
        displayName: 'Marketing Team',
        description: 'Microsoft Teams for Marketing',
        resourceType: 'group',
        originId: 'g_marketing_team',
        originSystem: 'AzureAD'
      }
    ]
  },
  {
    id: 'package_2',
    catalogId: 'catalog_2',
    displayName: 'Guest Collaboration Access',
    description: 'Limited access for external partners',
    isHidden: false,
    createdDateTime: new Date('2021-07-01'),
    modifiedDateTime: new Date('2024-03-10'),
    resourceScopes: [
      {
        id: 'resource_3',
        displayName: 'Partner Collaboration Site',
        description: 'External collaboration space',
        resourceType: 'sharePointOnline',
        originId: 'https://cloudonmars.sharepoint.com/sites/partners',
        originSystem: 'SharePointOnline'
      }
    ]
  }
];

// Mock Terms of Use
export const mockTermsOfUse: TermsOfUse[] = [
  {
    id: 'tou_1',
    displayName: 'Company Terms of Use',
    description: 'Standard terms of use for all employees',
    isPerDeviceAcceptanceRequired: false,
    termsExpiration: {
      startDateTime: new Date('2024-01-01'),
      frequency: 'P365D'
    },
    userReacceptRequiredFrequency: 'P365D',
    files: [
      {
        id: 'file_1',
        fileName: 'Terms_of_Use_2024.pdf',
        language: 'en-US',
        isDefault: true,
        fileData: {
          data: 'base64_encoded_pdf_content'
        }
      }
    ],
    acceptances: [
      {
        id: 'acceptance_1',
        userDisplayName: 'Sarah Chen',
        userPrincipalName: 'sarah.chen@cloudonmars.com',
        userEmail: 'sarah.chen@cloudonmars.com',
        state: 'accepted',
        recordedDateTime: new Date('2024-01-15T10:30:00Z'),
        expirationDateTime: new Date('2025-01-15')
      },
      {
        id: 'acceptance_2',
        userDisplayName: 'Michael Rodriguez',
        userPrincipalName: 'michael.rodriguez@cloudonmars.com',
        userEmail: 'michael.rodriguez@cloudonmars.com',
        state: 'accepted',
        recordedDateTime: new Date('2024-01-20T14:45:00Z'),
        expirationDateTime: new Date('2025-01-20')
      }
    ]
  },
  {
    id: 'tou_2',
    displayName: 'Guest Access Agreement',
    description: 'Terms for external guest users',
    isPerDeviceAcceptanceRequired: true,
    files: [
      {
        id: 'file_2',
        fileName: 'Guest_Agreement.pdf',
        language: 'en-US',
        isDefault: true,
        fileData: {
          data: 'base64_encoded_pdf_content'
        }
      }
    ],
    acceptances: []
  }
];

// Helper function to get user licenses summary
export const getUserLicensesSummary = () => {
  const licenseCounts: { [key: string]: number } = {};
  
  mockAzureADUsers.forEach(user => {
    user.assignedLicenses.forEach(license => {
      if (licenseCounts[license.name]) {
        licenseCounts[license.name]++;
      } else {
        licenseCounts[license.name] = 1;
      }
    });
  });
  
  return licenseCounts;
};

// Helper function to get authentication methods summary
export const getAuthMethodsSummary = () => {
  const methodCounts: { [key: string]: number } = {};
  
  mockAzureADUsers.forEach(user => {
    user.authenticationMethods.forEach(method => {
      if (methodCounts[method.type]) {
        methodCounts[method.type]++;
      } else {
        methodCounts[method.type] = 1;
      }
    });
  });
  
  return methodCounts;
};

// Helper function to get risk summary
export const getRiskSummary = () => {
  const riskCounts = {
    none: 0,
    low: 0,
    medium: 0,
    high: 0
  };
  
  mockAzureADUsers.forEach(user => {
    riskCounts[user.riskLevel]++;
  });
  
  return riskCounts;
};

// Helper function to get sync errors by severity
export const getSyncErrorsBySeverity = () => {
  const severityCounts = {
    Info: 0,
    Warning: 0,
    Error: 0,
    Critical: 0
  };
  
  mockAzureADSyncStatus.syncErrors.forEach(error => {
    severityCounts[error.severity]++;
  });
  
  return severityCounts;
};